import { useState } from 'react'
import { useQuery } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { useAuth } from '@/contexts/auth-context'
import { Loader2 } from 'lucide-react'

const PERIODS = [
  { label: '24h', days: 1 },
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
] as const

export default function AdminEventsPage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin
  const [days, setDays] = useState(7)
  const stats = useQuery(api.analytics.getEventVolume, isAdmin ? { days } : 'skip')

  if (!isAdmin) {
    return <div className="p-8 text-sm text-muted-foreground">Admin access required.</div>
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Events</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Product analytics — every <code className="text-xs">track()</code> call lands here.
          </p>
        </div>
        <div className="flex gap-1">
          {PERIODS.map((p) => (
            <button
              key={p.label}
              onClick={() => setDays(p.days)}
              className={`px-3 py-1.5 text-xs rounded border ${days === p.days ? 'bg-foreground text-background' : 'bg-background text-foreground hover:bg-muted'}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {!stats ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading event volume…
        </div>
      ) : stats.length === 0 ? (
        <div className="text-sm text-muted-foreground">No events in window.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground border-b">
            <tr>
              <th className="text-left py-2">Event</th>
              <th className="text-right py-2">Count</th>
              <th className="text-right py-2">Unique users</th>
              <th className="text-right py-2">Unique projects</th>
              <th className="text-right py-2">Last seen</th>
              <th className="text-left py-2 pl-4">Daily (last 14d)</th>
            </tr>
          </thead>
          <tbody>
            {stats.map((row) => (
              <tr key={row.name} className="border-b border-border/40 hover:bg-muted/40">
                <td className="py-2 font-mono text-xs">{row.name}</td>
                <td className="py-2 text-right">{row.count.toLocaleString()}</td>
                <td className="py-2 text-right">{row.uniqueUsers.toLocaleString()}</td>
                <td className="py-2 text-right">{row.uniqueWorkflows.toLocaleString()}</td>
                <td className="py-2 text-right text-xs text-muted-foreground">
                  {new Date(row.lastSeen).toLocaleString()}
                </td>
                <td className="py-2 pl-4">
                  <Sparkline daily={row.daily} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function Sparkline({ daily }: { daily: { date: string; count: number }[] }) {
  if (daily.length === 0) return <span className="text-xs text-muted-foreground">—</span>
  const max = Math.max(...daily.map((d) => d.count), 1)
  return (
    <div className="flex items-end gap-px h-8">
      {daily.map((d) => (
        <div
          key={d.date}
          title={`${d.date}: ${d.count}`}
          className="w-1.5 bg-blue-500/70 hover:bg-blue-500"
          style={{ height: `${(d.count / max) * 100}%` }}
        />
      ))}
    </div>
  )
}
